import { useMutation, useQueryClient } from 'react-query';
import notify from '../../../../../../../shared/feedback/notifications';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { useTelemetryContext } from '../../../../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../../../../telemetry/utils';
import ImageProcessingApi from '../../../api/ImageProcessingApi';
import { photoStatusQueryKey } from '../../entry/queries/use-query-photo-status';
import { imagesQueryKey } from '../../listImages/queries/use-query-images';
import { editedImageQueryKey } from './use-query-edited-image';

type ReplaceImageMutationArgs = {
    orderId: number;
    imageId: number;
    user: AuthenticatedUser;
    onReplaced: (newImageId: number) => void;
};

type ReplacedImage = {
    id: number;
};

export function useReplaceImageMutation({
    orderId,
    imageId,
    user,
    onReplaced
}: ReplaceImageMutationArgs) {
    let queryClient = useQueryClient();
    let { logger } = useTelemetryContext();

    return useMutation(
        (file: File): Promise<ReplacedImage> =>
            ImageProcessingApi.replaceEditedImage(orderId, imageId, file),
        {
            onSuccess: async (replaced) => {
                notify.success(Translate('image-processing/notification/images-uploaded'));
                await Promise.all([
                    queryClient.invalidateQueries([imagesQueryKey, { orderId }]),
                    queryClient.invalidateQueries([photoStatusQueryKey, { orderId }]),
                    queryClient.invalidateQueries([editedImageQueryKey, { orderId }])
                ]);
                onReplaced(replaced.id);
            },
            onError: (error, file) => {
                logger.logException(error, {
                    origin: 'ImageEditorImageProcessingPage.Mutation.replaceEditedImage',
                    userInfo: getUserInfo(user),
                    imageId,
                    filename: file.name
                });
                notify.failure(
                    Translate('image-processing/edited/file-upload/failed/text', {
                        filename: file.name
                    })
                );
            }
        }
    );
}
